import { axiosAPI as axios } from 'utils';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import { isOnline } from 'mobile-modules/offline';
import {
  createOfflineAttempt,
  finalizeOfflineAttempt,
  getCurrentOfflineAttempt,
  getOfflineAttempt,
  offlineAssessmentReport,
  rememberAttempt,
  submitOfflineActivity,
} from 'mobile-modules/offline/assessment';

function unwrap(response) {
  const payload = response?.data;

  if (payload && Object.prototype.hasOwnProperty.call(payload, 'data')) {
    return payload.data ?? null;
  }

  return payload ?? response ?? null;
}

const shouldFallbackOffline = (error) => (
  isMobileAppRuntime()
  && (!isOnline() || !Number(error?.response?.status || 0))
);

async function rememberResponseAttempt(value) {
  const attempt = value?.attempt || (value?._id ? value : null);
  if (isMobileAppRuntime() && attempt?._id) await rememberAttempt(attempt);
  return value;
}

const mobileAssessmentAPI = {
  async start(data) {
    if (isMobileAppRuntime() && !isOnline()) return createOfflineAttempt(data);
    try {
      return await rememberResponseAttempt(await axios.post('/api/mobile-v2/assessment/start', data).then(unwrap));
    } catch (error) {
      if (shouldFallbackOffline(error)) return createOfflineAttempt(data);
      throw error;
    }
  },
  async submitActivity(attemptId, data) {
    if (isMobileAppRuntime() && !isOnline()) return submitOfflineActivity(attemptId, data);
    try {
      return await rememberResponseAttempt(await axios.post(`/api/mobile-v2/assessment/${attemptId}/activities`, data).then(unwrap));
    } catch (error) {
      if (shouldFallbackOffline(error)) return submitOfflineActivity(attemptId, data);
      throw error;
    }
  },
  async finalize(attemptId, data = {}) {
    if (isMobileAppRuntime() && !isOnline()) return finalizeOfflineAttempt(attemptId, data);
    try {
      return await rememberResponseAttempt(await axios.post(`/api/mobile-v2/assessment/${attemptId}/finalize`, data).then(unwrap));
    } catch (error) {
      if (shouldFallbackOffline(error)) return finalizeOfflineAttempt(attemptId, data);
      throw error;
    }
  },
  async getMenu(attemptId) {
    if (isMobileAppRuntime() && !isOnline()) {
      const attempt = await getOfflineAttempt(attemptId);
      return attempt?.assessmentMenu || [];
    }
    try {
      return await axios.get(`/api/mobile-v2/assessment/${attemptId}/menu`).then(unwrap);
    } catch (error) {
      if (shouldFallbackOffline(error)) {
        const attempt = await getOfflineAttempt(attemptId);
        return attempt?.assessmentMenu || [];
      }
      throw error;
    }
  },
  async getReport(attemptId, data = {}) {
    if (isMobileAppRuntime() && !isOnline()) return offlineAssessmentReport(attemptId);
    try {
      return await rememberResponseAttempt(await axios.post(`/api/mobile-v2/assessment/${attemptId}/report`, data).then(unwrap));
    } catch (error) {
      if (shouldFallbackOffline(error)) return offlineAssessmentReport(attemptId);
      throw error;
    }
  },
  async createCustomContent(attemptId, data = {}) {
    if (isMobileAppRuntime() && !isOnline()) {
      throw new Error('Internet connection is required to get Custom Content. Please connect and try again.');
    }
    return axios.post(`/api/mobile-v2/custom-content/${attemptId}/first-level`, data).then(unwrap);
  },
  validateCustomContentSource(data = {}) {
    if (isMobileAppRuntime() && !isOnline()) {
      return Promise.resolve({ success: false, offline: true, message: 'Connect to the internet to validate Custom Content.' });
    }
    return axios.post('/api/mobile-v2/custom-content/diagnostics/validate', data).then(unwrap);
  },
  async getAttempt(attemptId) {
    if (isMobileAppRuntime() && !isOnline()) return getOfflineAttempt(attemptId);
    try {
      return await rememberResponseAttempt(await axios.get(`/api/mobile-v2/assessment/${attemptId}`).then(unwrap));
    } catch (error) {
      if (shouldFallbackOffline(error)) return getOfflineAttempt(attemptId);
      throw error;
    }
  },
  async getCurrentAttempt(params = {}) {
    if (isMobileAppRuntime() && !isOnline()) return getCurrentOfflineAttempt(params);
    try {
      return await rememberResponseAttempt(await axios.get('/api/mobile-v2/assessment/current', { params }).then(unwrap));
    } catch (error) {
      if (shouldFallbackOffline(error)) return getCurrentOfflineAttempt(params);
      throw error;
    }
  },
  getCurrentCustomLevel(familyMemberId) {
    if (isMobileAppRuntime() && !isOnline()) return Promise.resolve(null);
    return axios.get(`/api/mobile-v2/assessment/custom-levels/current/${familyMemberId || ''}`).then(unwrap);
  },
  getCurrentGeneratedCustomContentLevel(familyMemberId) {
    if (isMobileAppRuntime() && !isOnline()) return Promise.resolve(null);
    return axios.get(`/api/mobile-v2/custom-content/current/${familyMemberId || ''}`).then(unwrap);
  },
  async getCurrentReport(familyMemberId, solutionId) {
    if (isMobileAppRuntime() && !isOnline()) {
      const attempt = await getCurrentOfflineAttempt({ familyMemberId, solutionId });
      return attempt ? offlineAssessmentReport(attempt._id) : null;
    }
    return axios.get(`/api/mobile-v2/assessment/reports/current/${familyMemberId || ''}`, {
      params: { solutionId },
    }).then(unwrap);
  },
};

export default mobileAssessmentAPI;
